<template>
  <single-panel-page>
    <h5 v-text="formSubmitted ? 'Thank you for signing up!' : 'Sign up'" />

    <div
      v-for="error in errors.non_field_errors"
      :key="error"
      class="alert alert-danger alert-nobg text-left"
    >
      {{ error }}
    </div>

    <el-form
      v-if="!formSubmitted"
      ref="formRef"
      label-width="180px"
      :model="form"
    >
      <el-form-item
        label="Company name"
        prop="name"
        :error="errors.name"
        :rules="[
          {
            required: true,
            message: 'This field is required',
            trigger: 'blur',
          },
          {
            min: 2,
            message: 'Must be at least 2 characters long',
            trigger: 'blur',
          },
        ]"
      >
        <el-input v-model="form.name" autofocus @keyup.enter.native="submit" />
      </el-form-item>
      <el-form-item
        label="Your company e-mail"
        prop="email"
        :rules="[
          {
            required: true,
            message: 'This field is required',
            trigger: 'blur',
          },
          {
            type: 'email',
            message: 'Must be valid e-mail address',
            trigger: 'blur',
          },
        ]"
        :error="errors.email"
      >
        <el-input v-model="form.email" @keyup.enter.native="submit" />
      </el-form-item>
      <el-form-item
        label="Password"
        prop="password"
        :rules="[
          {
            required: true,
            message: 'This field is required',
            trigger: 'blur',
          },
          {
            min: 4,
            message: 'Must be at least 4 characters long',
            trigger: 'blur',
          },
        ]"
      >
        <el-input
          v-model="form.password"
          show-password
          @keyup.enter.native="submit"
          @focus="isPasswordFieldFocused = true"
          @blur="isPasswordFieldFocused = false"
        />
        <password :value="form.password" />
        <div class="el-form-item__error" v-if="errors.password.length">
          {{ errors.password }}
        </div>
      </el-form-item>
    </el-form>

    <div v-show="formSubmitted" class="row animated fadeIn">
      <div class="col-sm-12">
        <p class="text-success">
          To take care of startup data on the platform we verify all partners.
          This process usually takes up to 24 hours. Once your application has
          been processed, an activation link will be sent to you by email.
        </p>
      </div>
    </div>

    <TermsFormOrganizer
      class="mt-8"
      v-if="!formSubmitted"
      :all-terms-accepted.sync="allTermsAccepted"
      :show-errors="triedToSubmit"
      :mark-all="DEBUG"
    />

    <div v-if="!formSubmitted" class="row">
      <div class="col-sm-12 text-right">
        <el-button
          class="btn btn-block btn-primary text-uppercase"
          :disabled="inProgress"
          :loading="inProgress"
          @click="submit()"
        >
          <span>Sign up</span>
        </el-button>
      </div>
    </div>
  </single-panel-page>
</template>

<script>
import Auth from 'services/Auth'
import http from 'services/http'
import TermsFormOrganizer from '@/components/users/TermsFormOrganizer'
import { ref, reactive } from '@vue/composition-api'
import { fromPairs, toPairs } from 'lodash'
import router from 'routes'

function getInitialErrors() {
  return { password: '', email: '', name: '' }
}

const DEBUG = false

export default {
  components: {
    TermsFormOrganizer,
  },
  setup(props, { root }) {
    const isPasswordFieldFocused = ref(false)

    const triedToSubmit = ref(false)
    const inProgress = ref(false)
    const formSubmitted = ref(false)
    const form = reactive({
      name: '',
      email: '',
      password: '',
    })

    if (DEBUG) {
      const randomText = new Date().getTime().toString().substr(0, 10)
      form.name = `My company ${randomText}`
      form.email = `fake-${randomText}@fake.vestbee.com`
      form.password = 'test'
    }

    const formRef = ref(null)
    const allTermsAccepted = ref(false)

    const errors = ref(getInitialErrors())

    const submit = async () => {
      if (!allTermsAccepted.value) {
        root.$message.error('Must accept all terms first')
        return
      }
      console.log(' => submitted!')
      triedToSubmit.value = true

      try {
        await formRef.value.validate()
      } catch (err) {
        root.$message.error('Please correct form errors first')
        return
      }

      inProgress.value = true
      const { name, email, password } = form
      const req = Auth.signupOrganizer(email, password, name)
      try {
        await http.sleep(700)
        await req
        formSubmitted.value = true
        errors.value = getInitialErrors()
      } catch (error) {
        if (error && error.response && error.response.status === 400) {
          const data = error.response.data
          const responseErrors = toPairs(data).map(([field, errors]) => [
            field,
            errors[0],
          ])
          errors.value = { ...getInitialErrors(), ...fromPairs(responseErrors) }

          if (errors.value.email === 'IS_REGISTERED_ORGANIZER') {
            const featuredStartupsUrl = router.resolve({
              name: 'dashboardFeaturedStartups',
            }).href
            router.push({
              name: 'login',
              query: {
                email: form.email,
                code: 'IS_REGISTERED_ORGANIZER',
                next: featuredStartupsUrl,
              },
            })
          }
        }
      } finally {
        inProgress.value = false
      }
    }

    return {
      triedToSubmit,
      inProgress,
      allTermsAccepted,
      formSubmitted,
      form,
      formRef,
      submit,
      errors,
      DEBUG,
      isPasswordFieldFocused,
    }
  },
}
</script>
